import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Input, Modal, Table, Spin, message } from "antd"
import { EyeOutlined, DeleteOutlined, ExclamationCircleFilled } from "@ant-design/icons"
import ReactMarkdown from "react-markdown"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/ar"
import { ChatbotApi } from "../Apis/chatbotApi"
import { translations } from "../utils/Translation/staticTranslation"
import "../styles/Pages/chatbotChecker.css"

dayjs.extend(relativeTime)

const PAGE_SIZE = 10

const ChatbotChecker = () => {
  const lang = localStorage.getItem("language") || "ar"
  const isArabic = String(lang).toLowerCase().startsWith("ar")
  const t =
    translations[lang]?.chatbotCheckerPage ||
    translations.en.chatbotCheckerPage


  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detail, setDetail] = useState(null)

  const loadConversations = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ChatbotApi.getConversations({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch || undefined,
      })
      const result = res?.data?.result
      if (res?.data?.success && result && Array.isArray(result.results)) {
        setRows(result.results)
        setTotal(
          typeof result.count === "number"
            ? result.count
            : result.results.length,
        )
      } else {
        setRows([])
        setTotal(0)
      }
    } catch (e) {
      setRows([])
      setTotal(0)
      message.error(t.error_conversations)
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, t])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = search.trim()
        if (prev !== next) setPage(1)
        return next
      })
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  const openDetail = useCallback(
    async (row) => {
      setIsModalOpen(true)
      setDetailLoading(true)
      setDetail(null)
      try {
        const res = await ChatbotApi.getConversationDetail(row.id)
        if (res?.data?.success) setDetail(res.data.result)
      } catch (e) {
        message.error(t.error_detail)
      } finally {
        setDetailLoading(false)
      }
    },
    [t],
  )

  const handleDelete = useCallback(
    (row) => {
      Modal.confirm({
        title: t.delete_confirm_title,
        icon: <ExclamationCircleFilled />,
        content: t.delete_confirm_body,
        okText: t.delete_confirm_ok,
        okButtonProps: { danger: true },
        cancelText: t.delete_confirm_cancel,
        centered: true,
        onOk: async () => {
          try {
            await ChatbotApi.deleteConversation(row.id)
            message.success(t.delete_success)
            // If the deleted row is open in the modal, close it.
            setDetail((prev) =>
              prev?.conversation?.id === row.id ? null : prev,
            )
            loadConversations()
          } catch (e) {
            message.error(t.delete_failed)
            throw e // keep the confirm dialog's loading state honest
          }
        },
      })
    },
    [t, loadConversations],
  )

  const columns = useMemo(
    () => [
      {
        title: t.col_user,
        dataIndex: "user_name",
        key: "user",
        width: 220,
        render: (_, row) => (
          <div className="cbc-user-cell">
            <div className={`cbc-avatar avatar-${Number(row.id) % 6}`}>
              {(row.user_name || "U").slice(0, 2).toUpperCase()}
            </div>
            <div className="cbc-user-meta">
              <div className="cbc-user-name">{row.user_name || "—"}</div>
              <div className="cbc-user-email">{row.user_email || t.anonymous}</div>
            </div>
          </div>
        ),
      },
      {
        title: t.col_chat_id,
        dataIndex: "id",
        key: "id",
        width: 200,
        render: (id) => (
          <span className="cbc-chat-id" title={id ?? ""}>
            {id ?? "—"}
          </span>
        ),
      },
      {
        title: t.col_first_message,
        dataIndex: "preview",
        key: "preview",
        render: (preview) => (
          <span className="cbc-preview" title={preview}>
            {preview}
          </span>
        ),
      },
      {
        title: t.col_messages,
        dataIndex: "message_count",
        key: "message_count",
        align: "center",
        width: 110,
      },
      {
        title: t.col_started,
        dataIndex: "started_at",
        key: "started_at",
        width: 150,
        render: (value) =>
          value
            ? dayjs(value)
                .locale(isArabic ? "ar" : "en")
                .fromNow()
            : "—",
      },
      {
        title: t.col_actions,
        key: "actions",
        align: "center",
        width: 110,
        render: (_, row) => (
          <div className="cbc-actions-cell">
            <EyeOutlined
              className="cbc-eye-icon"
              onClick={() => openDetail(row)}
            />
            <DeleteOutlined
              className="cbc-delete-icon"
              onClick={() => handleDelete(row)}
            />
          </div>
        ),
      },
    ],
    [t, isArabic, openDetail, handleDelete],
  )

  return (
    <div className="cbc-page">
      <div className="cbc-header-row">
        <div>
          <h1 className="cbc-title">{t.page_title}</h1>
          <p className="cbc-subtitle">{t.page_subtitle}</p>
        </div>
      </div>

      <div className="cbc-panel">
        <div className="cbc-filters-row">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search_placeholder}
            className="cbc-search-input"
            allowClear
          />
        </div>
        <Spin spinning={loading}>
          <Table
            className="cbc-table"
            columns={columns}
            dataSource={rows.map((r) => ({ ...r, key: r.id }))}
            pagination={{
              current: page,
              total,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              position: ["bottomRight"],
              onChange: (nextPage) => setPage(nextPage),
            }}
            size="middle"
          />
        </Spin>
      </div>

      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          setDetail(null)
        }}
        footer={null}
        width={720}
        className={`cbc-detail-modal${isArabic ? " cbc-detail-modal--ar" : ""}`}
        closeIcon={<span className="cbc-modal-close">×</span>}
      >
        <Spin spinning={detailLoading}>
          {detail?.conversation && (
            <>
              <div className="cbc-modal-header">
                <div
                  className={`cbc-avatar cbc-modal-avatar avatar-${Number(detail.conversation.id) % 6}`}
                >
                  {(detail.conversation.user_name || "U")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div>
                  <div className="cbc-modal-name">
                    {detail.conversation.user_name || t.anonymous}
                  </div>
                  <div className="cbc-modal-sub">
                    {detail.conversation.user_email || t.anonymous} ·{" "}
                    {dayjs(detail.conversation.started_at)
                      .locale(isArabic ? "ar" : "en")
                      .format("YYYY-MM-DD HH:mm")}
                  </div>
                </div>
              </div>

              <div className="cbc-transcript">
                {(detail.messages || []).map((m) => (
                  <div
                    key={m.id}
                    className={`cbc-msg cbc-msg-${m.from === "user" ? "user" : "bot"}`}
                  >
                    <div className="cbc-msg-bubble">
                      {m.imageUrl && (
                        <a href={m.imageUrl} target="_blank" rel="noopener noreferrer">
                          <img
                            src={m.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{
                              display: "block",
                              maxWidth: "220px",
                              maxHeight: "160px",
                              borderRadius: "8px",
                              marginBottom: m.text ? "6px" : 0,
                              objectFit: "cover",
                            }}
                          />
                        </a>
                      )}
                      {m.from === "bot" ? (
                        <ReactMarkdown>{m.text}</ReactMarkdown>
                      ) : (
                        m.text
                      )}
                    </div>
                    <div className="cbc-msg-meta">
                      {m.from === "bot" ? t.sender_bot : t.sender_user}
                      {m.response_ms
                        ? ` · ${(m.response_ms / 1000).toFixed(1)}s`
                        : ""}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Spin>
      </Modal>
    </div>
  )
}

export default ChatbotChecker
